/**
 * Internal helpers for the client-side AI resolver. Re-uses the same
 * shape the backend used in `apps/api/.../proposed-changes.ts` +
 * `apps/api/.../dispatcher.ts`. Both files were deleted when AI
 * resolution moved to the frontend; the constants and the small
 * `ai_suggestions[]` array manipulators are re-implemented here so the
 * resolver stays self-contained.
 */

import * as Sentry from "@sentry/react";
import type { PreOrderFlow } from "@/app/api/types/preOrder";

// ---------- constants (mirror backend `dispatcher.ts`) ----------

export const HEADER_METADATA_FIELDS = new Set([
  "sourceDocuments",
  "customFields",
]);
export const HEADER_METADATA_BLOB_FIELD = "preOrderMetadata";
export const FLOW_METADATA_BLOB_FIELD = "flowMetadata";

const AI_SUGGESTIONS_KEY = "ai_suggestions";
const AI_SUGGESTION_LEGACY_KEY = "ai_suggestion";

export const PRE_ORDER_FLOW_STATUS_PENDING = 0;

// ---------- block shape ----------

export interface AiFieldChange {
  from: unknown;
  to: unknown;
  finalTo?: unknown;
}

export interface AiSuggestionBlock {
  index: number;
  status: "pending" | "accepted" | "declined" | "modified";
  type: "add" | "modify" | "delete";
  changes?: Record<string, AiFieldChange>;
  batchId: string;
  proposedBy: string;
  proposedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export type Metadata = Record<string, unknown> | null | undefined;

// ---------- proposed-changes helpers ----------

export function readBlocks(metadata: Metadata): AiSuggestionBlock[] {
  if (!metadata || typeof metadata !== "object") return [];
  const arr = metadata[AI_SUGGESTIONS_KEY];
  if (Array.isArray(arr)) {
    const wellFormed = arr.filter(isWellFormedBlock);
    if (wellFormed.length < arr.length) {
      warnDroppedBlocks(arr.filter((b) => !isWellFormedBlock(b)));
    }
    return wellFormed;
  }
  const legacy = metadata[AI_SUGGESTION_LEGACY_KEY];
  if (legacy && typeof legacy === "object" && !Array.isArray(legacy)) {
    const wrapped = wrapLegacy(legacy as Record<string, unknown>);
    if (wrapped) return [wrapped];
    warnDroppedBlocks([legacy]);
  }
  return [];
}

/**
 * A dropped PENDING-looking block means AI data exists that no UI will
 * surface and no accept/decline will resolve — either legacy rows written
 * before the audit fields existed, or a writer bug. That must not stay
 * invisible (a silent drop reads as "no AI here"), so log once per unique
 * payload. Resolved/history blocks are dropped silently: they carry no
 * pending intent, losing them changes nothing.
 */
const warnedDroppedBlocks = new Set<string>();
function warnDroppedBlocks(dropped: unknown[]): void {
  for (const block of dropped) {
    const status = (block as { status?: unknown } | null)?.status;
    if (status !== "pending") continue;
    const key = JSON.stringify(block);
    if (warnedDroppedBlocks.has(key)) continue;
    warnedDroppedBlocks.add(key);
    const message =
      "aiResolveLocally: dropped a malformed pending ai_suggestions block";
    console.warn(message, block);
    Sentry.captureMessage(message, {
      level: "warning",
      extra: { block },
    });
  }
}

export function latestPending(
  blocks: AiSuggestionBlock[],
): AiSuggestionBlock | null {
  const last = blocks.at(-1);
  if (!last) return null;
  return last.status === "pending" ? last : null;
}

export function writeBlocks(
  metadata: Metadata,
  blocks: AiSuggestionBlock[],
): Record<string, unknown> {
  const base: Record<string, unknown> =
    metadata && typeof metadata === "object" ? metadata : {};
  const next: Record<string, unknown> = { ...base };
  next[AI_SUGGESTIONS_KEY] = blocks;
  delete next[AI_SUGGESTION_LEGACY_KEY];
  return next;
}

export function replaceBlock(
  blocks: AiSuggestionBlock[],
  index: number,
  next: AiSuggestionBlock,
): AiSuggestionBlock[] {
  const out = [...blocks];
  const pos = out.findIndex((b) => b.index === index);
  if (pos < 0) throw new Error(`replaceBlock: no entry with index ${index}`);
  out[pos] = next;
  return out;
}

export function markResolved(
  block: AiSuggestionBlock,
  next: Exclude<AiSuggestionBlock["status"], "pending">,
  resolvedBy: string,
  resolvedAt: string,
  finalChanges?: Record<string, unknown>,
): AiSuggestionBlock {
  const out: AiSuggestionBlock = {
    ...block,
    status: next,
    resolvedAt,
    resolvedBy,
  };
  if (next === "modified" && finalChanges && block.changes) {
    const nextChanges: Record<string, AiFieldChange> = {};
    for (const [field, change] of Object.entries(block.changes)) {
      if (field in finalChanges) {
        nextChanges[field] = { ...change, finalTo: finalChanges[field] };
      } else {
        nextChanges[field] = change;
      }
    }
    out.changes = nextChanges;
  }
  return out;
}

/**
 * Flip a pending block to its resolved status in-array. Thin wrapper over
 * `replaceBlock` + `markResolved` so every accept/decline branch resolves
 * a block — and records `finalTo` overrides — the same way.
 */
export function resolveBlocks(
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  status: Exclude<AiSuggestionBlock["status"], "pending">,
  resolvedBy: string,
  resolvedAt: string,
  finalChanges?: Record<string, unknown>,
): AiSuggestionBlock[] {
  return replaceBlock(
    blocks,
    pending.index,
    markResolved(pending, status, resolvedBy, resolvedAt, finalChanges),
  );
}

/**
 * THE single place an AI-resolution metadata write is assembled, so the
 * blob-preservation + block-stamping invariants can't diverge per branch:
 *
 *   1. Start from the row's live metadata (`flow_metadata` /
 *      `pre_order_metadata`).
 *   2. Layer each metadata blob over it in precedence order — later layers
 *      win. Callers pass the AI-applied blob, the user-staged blob, any
 *      override blob, …; `null`/`undefined` layers are skipped.
 *   3. Stamp the flipped `ai_suggestions[]` LAST, so a stale
 *      `ai_suggestions` array carried inside any blob layer (the user blob
 *      ships the whole `flow_metadata`, pending blocks included) can never
 *      survive the write.
 *
 * The m3-comment-drop bug was a branch that rebuilt metadata from the live
 * row only and forgot the user-staged blob layer (step 2). Funnelling every
 * branch through here makes that omission impossible to express silently —
 * the layers are an explicit argument, not an easy-to-skip spread.
 */
export function assembleResolvedMetadata(
  liveMetadata: Metadata,
  blobLayers: ReadonlyArray<Record<string, unknown> | null | undefined>,
  flipped: AiSuggestionBlock[],
): Record<string, unknown> {
  const merged: Record<string, unknown> = isPlainObject(liveMetadata)
    ? { ...liveMetadata }
    : {};
  for (const layer of blobLayers) {
    if (!layer) continue;
    // `m3_comments` must be DEEP-merged, not whole-object replaced: a layer
    // often carries only the variant the user just edited (e.g. `c2`), and a
    // shallow `Object.assign` would drop the sibling variants the UI never
    // surfaces — notably the CO04 prep-slip `c4` ("de côté déjà prêt").
    // (STK-572.) A key present in the layer — including "" — overrides, so a
    // deliberate clear of one variant still propagates; an absent key keeps
    // the live value.
    const mergedM3 = mergeM3CommentsLayer(
      merged.m3_comments,
      layer.m3_comments,
    );
    Object.assign(merged, layer);
    if (mergedM3 !== undefined) merged.m3_comments = mergedM3;
  }
  return writeBlocks(merged, flipped);
}

/**
 * Deep-merge a layer's `m3_comments` over the accumulated value so a partial
 * edit can't drop sibling variants (STK-572). Returns the merged sub-dict, or
 * `undefined` when the layer doesn't carry an `m3_comments` object — in which
 * case the caller leaves whatever `Object.assign` produced untouched.
 */
function mergeM3CommentsLayer(
  live: unknown,
  incoming: unknown,
): Record<string, unknown> | undefined {
  if (!isPlainObject(incoming)) return undefined;
  const base = isPlainObject(live) ? live : {};
  return { ...base, ...incoming };
}

/**
 * Strip the vestigial `ai_suggestions` (and legacy `ai_suggestion`) keys from
 * every metadata blob in a resolved batch. The typed sidecar ROW is the
 * durable source of truth now; the merge math still writes a flipped block
 * into the metadata blobs (it's shared with the legacy path), so we remove it
 * here before the batch ships — otherwise push-batch would re-seed the
 * `ai_suggestions[]` JSON the projection was built to replace.
 *
 * Returns a new batch; never mutates the input. A metadata blob that becomes
 * empty after stripping is left as `{}` rather than dropped — push-batch
 * treats an explicit empty object as "no metadata change beyond what's
 * already merged", and the column writes alongside it still apply.
 */
export function stripAiBlocksFromBatch<
  B extends {
    preOrderUpdate?: Record<string, unknown>;
    flowChanges: Array<{
      op: string;
      id?: string;
      data?: Record<string, unknown>;
    }>;
  },
>(batch: B): B {
  const preOrderUpdate = stripBlobInPatch(
    batch.preOrderUpdate,
    HEADER_METADATA_BLOB_FIELD,
  );
  const flowChanges = batch.flowChanges.map((change) => {
    if (change.op !== "update" || !change.data) return change;
    const data = stripBlobInPatch(change.data, FLOW_METADATA_BLOB_FIELD);
    return { ...change, data };
  });
  return { ...batch, preOrderUpdate, flowChanges } as B;
}

function stripBlobInPatch<P extends Record<string, unknown> | undefined>(
  patch: P,
  blobField: string,
): P {
  if (!patch) return patch;
  const blob = patch[blobField];
  if (!isPlainObject(blob)) return patch;
  if (!(AI_SUGGESTIONS_KEY in blob) && !(AI_SUGGESTION_LEGACY_KEY in blob)) {
    return patch;
  }
  const nextBlob = { ...blob };
  delete nextBlob[AI_SUGGESTIONS_KEY];
  delete nextBlob[AI_SUGGESTION_LEGACY_KEY];
  return { ...patch, [blobField]: nextBlob } as P;
}

const VALID_STATUSES = new Set(["pending", "accepted", "declined", "modified"]);
const VALID_TYPES = new Set(["add", "modify", "delete"]);

function isWellFormedBlock(x: unknown): x is AiSuggestionBlock {
  if (!x || typeof x !== "object") return false;
  const o = x as Partial<AiSuggestionBlock>;
  if (
    typeof o.index !== "number" ||
    !Number.isInteger(o.index) ||
    o.index <= 0
  ) {
    return false;
  }
  if (
    typeof o.batchId !== "string" ||
    typeof o.proposedBy !== "string" ||
    typeof o.proposedAt !== "string"
  ) {
    return false;
  }
  if (typeof o.status !== "string" || !VALID_STATUSES.has(o.status)) {
    return false;
  }
  if (typeof o.type !== "string" || !VALID_TYPES.has(o.type)) {
    return false;
  }
  // `changes` is required for type:modify and forbidden for type:delete.
  // type:add may have changes (override hints) or not. When present, it
  // must be a non-null object whose values look like `{from, to}` —
  // otherwise downstream readers crash on `change.to`.
  if (o.changes !== undefined) {
    if (
      !o.changes ||
      typeof o.changes !== "object" ||
      Array.isArray(o.changes)
    ) {
      return false;
    }
    for (const v of Object.values(o.changes)) {
      if (!v || typeof v !== "object" || !("to" in v)) return false;
    }
  } else if (o.type === "modify") {
    return false;
  }
  return true;
}

function wrapLegacy(legacy: Record<string, unknown>): AiSuggestionBlock | null {
  if (
    typeof legacy.batchId !== "string" ||
    typeof legacy.proposedBy !== "string" ||
    typeof legacy.proposedAt !== "string" ||
    typeof legacy.status !== "string" ||
    typeof legacy.type !== "string"
  ) {
    return null;
  }
  const out: AiSuggestionBlock = {
    index: 1,
    status: legacy.status as AiSuggestionBlock["status"],
    type: legacy.type as AiSuggestionBlock["type"],
    batchId: legacy.batchId,
    proposedBy: legacy.proposedBy,
    proposedAt: legacy.proposedAt,
  };
  if (legacy.changes && typeof legacy.changes === "object") {
    out.changes = legacy.changes as Record<string, AiFieldChange>;
  }
  return out;
}

// ---------- value/structural helpers ----------

export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Pull a metadata-blob field (`flowMetadata` / `preOrderMetadata`) out
 * of a field-write map and return it. Mutating delete so the caller
 * can spread the remaining columns without the blob landing as a
 * literal column value (Drizzle would attempt to write the entire
 * object back, overwriting `ai_suggestions[]`).
 */
export function extractMetadataBlob(
  fields: Record<string, unknown>,
  fieldName: string,
): Record<string, unknown> | null {
  if (!(fieldName in fields)) return null;
  const raw = fields[fieldName];
  delete fields[fieldName];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

export function splitHeaderFields(fields: Record<string, unknown>): {
  columnWrites: Record<string, unknown>;
  metadataWrites: Record<string, unknown>;
} {
  const columnWrites: Record<string, unknown> = {};
  const metadataWrites: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (k === HEADER_METADATA_BLOB_FIELD) continue;
    if (HEADER_METADATA_FIELDS.has(k)) metadataWrites[k] = v;
    else columnWrites[k] = v;
  }
  return { columnWrites, metadataWrites };
}

// ---------- snake↔camel mapping for flow columns ----------
//
// Frontend `PreOrder.flows[i]` is snake_case (`product_id`,
// `flow_metadata`) but AI block changes carry camelCase keys
// (`productId`, `flowMetadata`). We translate AI keys to snake_case to
// look up the row's current value when doing equality detection for
// type:add overrides.

const FLOW_CAMEL_TO_SNAKE: Record<string, keyof PreOrderFlow> = {
  productId: "product_id",
  partnerId: "partner_id",
  unitId: "unit_id",
  quantity: "quantity",
  weight: "weight",
  pricePerWeight: "price_per_weight",
  comment: "comment",
  hiddenComment: "hidden_comment",
  status: "status",
  flowIndex: "index",
  deliveryDate: "delivery_date",
  orderDate: "order_date",
  flowMetadata: "flow_metadata",
};

export function flowCamelKeyToSnake(field: string): keyof PreOrderFlow | null {
  if (Object.hasOwn(FLOW_CAMEL_TO_SNAKE, field)) {
    return FLOW_CAMEL_TO_SNAKE[field];
  }
  return null;
}

// ---------- snake↔camel mapping for header columns ----------
//
// Same problem as the flow map above, for the pre-order header. AI block
// changes carry camelCase column keys (`partnerId`, `deliveryDate`,
// `comment`, …); the live `PreOrder` row is snake_case. We translate so
// the accept-time three-way merge can read the user's CURRENT live column
// value — header edits land via a live `useUpdatePreOrder` mutation (not
// the modify buffer), so the resolver must read them off the live row to
// detect a user override and let it win over the AI's `to`.

const HEADER_CAMEL_TO_SNAKE: Record<string, string> = {
  partnerId: "partner_id",
  deliveryDate: "delivery_date",
  orderDate: "order_date",
  comment: "comment",
  hiddenComment: "hidden_comment",
  preOrderNumber: "pre_order_number",
  customerPoNumber: "customer_po_number",
  employeeId: "employee_id",
  locationId: "location_id",
  routeId: "route_id",
  shippingAddressId: "shipping_address_id",
};

export function headerCamelKeyToSnake(field: string): string | null {
  if (Object.hasOwn(HEADER_CAMEL_TO_SNAKE, field)) {
    return HEADER_CAMEL_TO_SNAKE[field];
  }
  return null;
}
